import { useTranslation } from 'react-i18next'
import { useSelector, shallowEqual } from 'react-redux'
import { path } from 'ramda'
import { useCallback } from 'react'
import moment from 'moment-timezone'
import petrovich from 'petrovich'
import { prop } from 'ramda'

import FinancesHistory from 'app/common/components/Finances/FinancesHistory'
import { getIsAppConnected } from 'app/selectors/applications'
import * as services from 'app/common/constants/services'
import SimpleDate from 'app/common/classes/simple-date'

import {
  cafeteriaFinancesPathMask,
  params as p,
  createPath,
  getSchoolTypeExact,
  getStudentIdExact,
  getDateExact,
  monthFormat,
  dateFormat,
  isMonthly,
} from 'app/pages/routePaths'

import selectPerson, { type Person } from '../selectPerson'
import FinancesSubNavigation from './FinancesSubNavigation'
import getBaseUrl from '../getBaseUrl'
import useSchoolAsCamp from '../useSchoolAsCamp'

type Props = {
  params: {
    id: string
    date: string
    schoolType: string
  }
  router: {
    replace: (pathname: string) => void
    push: (pathname: string) => void
  }
}

const FinancesPage = ({ params, router: { push } }: Props) => {
  const { t } = useTranslation('finances')

  const date = getDateExact(params)
  const id = getStudentIdExact(params)
  const schoolType = getSchoolTypeExact(params)

  const person: Person = useSelector((state) => selectPerson(state, id))
  const isSubscribed = useSelector((state) =>
    getIsAppConnected(state, { id, app: services.APP_CAFETERIA }),
  )
  const hasPaymentOrders = useSelector(
    path(['students', 'data', id, 'hasPaymentOrders']),
    shallowEqual,
  )

  const handleChangeDate = useCallback(
    (newDate: string, newDateFormat: string, daily: boolean = false) => {
      push(
        createPath(
          {
            [p.studentId]: id,
            [p.schoolType]: schoolType,
            [p.date]: moment(newDate, newDateFormat).format(daily ? dateFormat : monthFormat),
          },
          cafeteriaFinancesPathMask,
        ),
      )
    },
    [id, push, schoolType],
  )

  const baseUrlRoot = getBaseUrl({ id, schoolType })
  const school = useSchoolAsCamp(id, schoolType)

  const dateType = isMonthly(date) ? 'month' : 'day'
  const simpleDate = new SimpleDate(date, isMonthly(date) ? monthFormat : dateFormat)

  const datePresentative =
    dateType === 'day'
      ? simpleDate.getStringFormatted('DD.MM.YYYY')
      : simpleDate.getStringFormatted('MMMM YYYY')

  const header = t('finances_student_header', {
    name: prop('first', petrovich(person.name, 'genitive')),
    date: datePresentative.toLocaleLowerCase(),
  })

  return (
    <FinancesHistory
      date={simpleDate}
      dateType={dateType}
      header={header}
      school={school}
      person={person}
      bannerPlaceId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
      blockDetails={
        isSubscribed
          ? ''
          : t(
              'block_details_message',
              'Для просмотра детализации необходима подписка на приложение',
            ) || undefined
      }
      subNavigation={hasPaymentOrders ? <FinancesSubNavigation baseUrl={baseUrlRoot} /> : null}
      onChangeDate={handleChangeDate}
    />
  )
}

export default FinancesPage
